<script setup lang="ts">
/**
 * The reasoning behind a decision, folded under "Show details" (#1760): closed until the reader asks, so a card reads
 * as its one-line lead and what it asks of them, and the why is one press away. The toggle is a real button that
 * says whether it is open (`aria-expanded`) and which region it opens (`aria-controls`); the folded content stays in
 * the page while closed, only hidden, so find in page and a later open lose nothing.
 *
 * ZhDecisionCard folds its `details` slot with it; a form inside a card (a question's context) folds its own with it
 * too, so every fold on a decision looks and behaves the same.
 *
 * A page can give the fold its own `label` (#1762: "How this works" for the engine detail under a page intro). The
 * label is the toggle's text whether open or closed; the marker and `aria-expanded` say which. With no label the
 * toggle keeps the decision cards' "Show details" / "Hide details".
 */
import { ref, useId } from 'vue'

defineProps<{ label?: string }>()

const regionId = `zh-details-fold-${useId()}`
const open = ref(false)
</script>

<template>
    <div class="zh-details-fold" :data-open="open ? 'true' : undefined">
        <button
            type="button"
            class="zh-details-fold__toggle"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="regionId"
            @click="open = !open"
        >
            {{ label || (open ? 'Hide details' : 'Show details') }}
        </button>
        <div v-show="open" :id="regionId" class="zh-details-fold__body">
            <slot />
        </div>
    </div>
</template>

<style scoped>
.zh-details-fold {
    display: grid;
    gap: 0.375rem;
}

/* The system label (BG §19.3) as a control: mono, uppercase, the muted ink, with its disclosure marker beside it. */
.zh-details-fold__toggle {
    justify-self: start;
    padding: 0;
    border: 0;
    background: none;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    cursor: pointer;
}

.zh-details-fold__toggle::before {
    content: "▸\00a0" / "";
}

.zh-details-fold[data-open] .zh-details-fold__toggle::before {
    content: "▾\00a0" / "";
}

.zh-details-fold__toggle:hover,
.zh-details-fold__toggle:focus-visible {
    color: var(--ink-1);
}

/* A keyboard reader sees where they are, in the same ring the theme's buttons use. */
.zh-details-fold__toggle:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}

.zh-details-fold__body {
    display: grid;
    gap: 0.5rem;
    padding-left: 0.75rem;
    border-left: 1px solid var(--hair);
    color: var(--ink-2);
}
</style>
